import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Box,
  Grid,
  Paper,
  TextField,
  IconButton,
  Typography,
  InputAdornment,
  AppBar,
  Toolbar,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  Drawer,
  useTheme,
  useMediaQuery,
  Fab,
  Slide,
  Zoom,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  Search as SearchIcon,
  ExitToApp as LogoutIcon,
  Settings as SettingsIcon,
  AttachFile as AttachFileIcon,
  PersonAdd as PersonAddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import io from 'socket.io-client';
import ContactList from './ContactList';
import MessageBubble from './MessageBubble';
import UserStatus from './UserStatus';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';

const Chat = () => {
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [socket, setSocket] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [mediaUploadAnchor, setMediaUploadAnchor] = useState(null);
  const [friendRequests, setFriendRequests] = useState([]);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [friendRequestsOpen, setFriendRequestsOpen] = useState(false);
  const fileInputRef = useRef();
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user: authUser } = useContext(AuthContext);
  const [settingsAnchor, setSettingsAnchor] = useState(null);

  // Parse user data once on mount
  useEffect(() => {
    const userData = localStorage.getItem('user');
    console.log('User data from localStorage:', userData);
    
    try {
      const parsedUser = userData ? JSON.parse(userData) : null;
      console.log('Parsed user data:', parsedUser);
      if (!parsedUser || !parsedUser.id) {
        console.log('No valid user data found, redirecting to login');
        navigate('/login');
        return;
      }
      setUser(parsedUser);
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/login');
    }
  }, [navigate]);

  // Socket connection
  useEffect(() => {
    if (!user) return;

    console.log('Connecting to socket server...');
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://chat.aunimeda.com';
    const newSocket = io(baseUrl, {
      auth: {
        token: localStorage.getItem('token')
      },
      path: '/socket.io',
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      transports: ['polling', 'websocket'],
      withCredentials: true,
      forceNew: true,
      rejectUnauthorized: false
    });

    let isConnected = false;

    newSocket.on('connect', () => {
      if (!isConnected) {
        console.log('Connected to socket server');
        isConnected = true;
        newSocket.emit('join', user.id);
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      isConnected = false;
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from socket server');
      isConnected = false;
    });

    newSocket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    newSocket.on('users_online', (users) => {
      console.log('Users online:', users);
      setOnlineUsers(users);
    });

    newSocket.on('user_connected', (userId) => {
      console.log('User connected:', userId);
      setOnlineUsers(prev => {
        if (!prev.includes(userId)) {
          return [...prev, userId];
        }
        return prev;
      });
    });

    newSocket.on('user_disconnected', (userId) => {
      console.log('User disconnected:', userId);
      setOnlineUsers(prev => prev.filter(id => id !== userId));
    });

    setSocket(newSocket);

    return () => {
      console.log('Cleaning up socket connection');
      if (newSocket) {
        newSocket.off('connect');
        newSocket.off('connect_error');
        newSocket.off('disconnect');
        newSocket.off('error');
        newSocket.off('users_online');
        newSocket.off('user_connected');
        newSocket.off('user_disconnected');
        newSocket.off('receiveMessage');
        newSocket.close();
      }
    };
  }, [user]);

  // Message receiving
  useEffect(() => {
    if (socket && user && selectedContact) {
      socket.on('receiveMessage', (message) => {
        console.log('Received message:', message);
        if (message.sender === selectedContact._id || message.sender._id === selectedContact._id) {
          setMessages(prev => [...prev, message]);
          socket.emit('markAsRead', { messageId: message._id });
        }
      });

      return () => {
        socket.off('receiveMessage');
      };
    }
  }, [socket, selectedContact, user]);

  // Load contacts
  useEffect(() => {
    if (user) {
      console.log('Loading contacts...');
      loadContacts();
    }
  }, [user]);

  // Load messages
  useEffect(() => {
    if (selectedContact) {
      console.log('Loading messages for contact:', selectedContact._id);
      loadMessages(selectedContact._id);
    }
  }, [selectedContact]);

  // Scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadContacts = async () => {
    try {
      console.log('Fetching contacts...');
      const response = await api.get('/users/contacts');
      console.log('Contacts loaded:', response.data);
      
      // Get last messages for each contact
      const contactsWithLastMessages = await Promise.all(
        response.data.map(async (contact) => {
          try {
            const messagesResponse = await api.get(`/messages/${contact._id}?limit=1`);
            return {
              ...contact,
              isFriend: true,
              lastMessage: messagesResponse.data[0] || null
            };
          } catch (err) {
            console.error(`Error loading last message for contact ${contact._id}:`, err);
            return {
              ...contact,
              isFriend: true,
              lastMessage: null
            };
          }
        })
      );

      // Sort contacts by last message time
      const sortedContacts = contactsWithLastMessages.sort((a, b) => {
        if (!a.lastMessage && !b.lastMessage) return 0;
        if (!a.lastMessage) return 1;
        if (!b.lastMessage) return -1;
        return new Date(b.lastMessage.createdAt) - new Date(a.lastMessage.createdAt);
      });

      setContacts(sortedContacts);
    } catch (err) {
      console.error('Error loading contacts:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  const loadMessages = async (contactId) => {
    try {
      console.log('Fetching messages for contact:', contactId);
      const response = await api.get(`/messages/${contactId}`);
      console.log('Messages loaded:', response.data);
      setMessages(response.data);
      if (socket) {
        socket.emit('markAsRead', { senderId: contactId });
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedContact) return;

    try {
      // Debug logging
      console.log('Selected contact:', selectedContact);
      console.log('User data:', {
        id: user.id,
        _id: user._id,
        username: user.username,
        avatar: user.avatar
      });

      const messageData = {
        receiverId: selectedContact._id,
        content: newMessage.trim()
      };
      console.log('Sending message data:', messageData);

      const response = await api.post('/messages', messageData);
      console.log('Message sent:', response.data);

      // Update messages list with complete sender info
      const messageWithSender = {
        ...response.data,
        sender: {
          _id: user.id,
          username: user.username,
          avatar: user.avatar
        }
      };

      setMessages(prev => [...prev, messageWithSender]);
      setNewMessage('');

      // Update contact's last message
      setContacts(prevContacts => {
        const updatedContacts = prevContacts.map(contact => {
          if (contact._id === selectedContact._id) {
            return {
              ...contact,
              lastMessage: messageWithSender
            };
          }
          return contact;
        });

        return updatedContacts.sort((a, b) => {
          if (!a.lastMessage && !b.lastMessage) return 0;
          if (!a.lastMessage) return 1;
          if (!b.lastMessage) return -1;
          return new Date(b.lastMessage.createdAt) - new Date(a.lastMessage.createdAt);
        });
      });

      if (socket) {
        socket.emit('sendMessage', messageWithSender);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      console.error('Request data:', err.config?.data);
      console.error('Response data:', err.response?.data);
      setNotification({
        open: true,
        message: `Failed to send message: ${err.response?.data?.message || err.message}`,
        severity: 'error'
      });
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadContacts();
      return;
    }

    try {
      console.log('Searching users with query:', searchQuery);
      const response = await api.get(`/users/search?username=${searchQuery}`);
      console.log('Search results:', response.data);
      // Add isFriend flag to search results
      const searchResults = response.data.map(contact => ({
        ...contact,
        isFriend: user.friends?.includes(contact._id) || contacts.some(c => c._id === contact._id)
      }));
      setContacts(searchResults);
    } catch (err) {
      console.error('Error searching users:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  const handleLogout = () => {
    console.log('Logging out...');
    if (socket) {
      socket.close();
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleSettings = () => {
    setSettingsAnchor(null);
    navigate('/settings');
  };

  const handleCloseSettings = () => {
    setSettingsAnchor(null);
  };

  // Load friend requests
  useEffect(() => {
    if (user) {
      loadFriendRequests();
    }
  }, [user]);

  const loadFriendRequests = async () => {
    try {
      const response = await api.get('/users/friend-requests');
      setFriendRequests(response.data);
    } catch (err) {
      console.error('Error loading friend requests:', err);
    }
  };

  const handleSendFriendRequest = async (contactId) => {
    try {
      await api.post(`/users/friend-request/${contactId}`);
      setNotification({
        open: true,
        message: 'Friend request sent successfully',
        severity: 'success'
      });
      // Refresh the contacts list to update the UI
      if (searchQuery) {
        handleSearch();
      } else {
        loadContacts();
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.response?.data?.message || 'Failed to send friend request',
        severity: 'error'
      });
    }
  };

  const handleAcceptFriend = async (userId) => {
    try {
      await api.post(`/users/accept-friend/${userId}`);
      setNotification({
        open: true,
        message: 'Friend request accepted',
        severity: 'success'
      });
      loadFriendRequests();
      loadContacts();
    } catch (err) {
      setNotification({
        open: true,
        message: err.response?.data?.message || 'Failed to accept friend request',
        severity: 'error'
      });
    }
  };

  const handleRejectFriend = async (userId) => {
    try {
      await api.post(`/users/reject-friend/${userId}`);
      setNotification({
        open: true,
        message: 'Friend request rejected',
        severity: 'success'
      });
      loadFriendRequests();
    } catch (err) {
      setNotification({
        open: true,
        message: err.response?.data?.message || 'Failed to reject friend request',
        severity: 'error'
      });
    }
  };

  const handleMediaUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedContact) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('receiverId', selectedContact._id);

    try {
      const response = await api.post('/messages/media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessages(prev => [...prev, response.data]);
      if (socket) {
        socket.emit('sendMessage', response.data);
      }
    } catch (err) {
      console.error('Error uploading media:', err);
      setNotification('Failed to upload media');
    }

    setMediaUploadAnchor(null);
  };

  // Вспомогательный компонент для списка контактов
  const ContactListContent = () => {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
          />
        </Box>
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <ContactList
            contacts={contacts}
            selectedContact={selectedContact}
            onSelectContact={(contact) => {
              setSelectedContact(contact);
              if (isMobile) {
                setMobileOpen(false);
              }
            }}
            onlineUsers={onlineUsers}
            onSendFriendRequest={handleSendFriendRequest}
          />
        </Box>
      </Box>
    );
  };

  // Если нет данных пользователя, ничего не показываем (будет редирект в useEffect)
  if (!user) {
    console.log('No user data, rendering null');
    return null;
  }

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden',
      bgcolor: 'background.default'
    }}>
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          bgcolor: theme.palette.mode === 'dark' 
            ? 'background.paper' 
            : 'primary.main',
          color: theme.palette.mode === 'dark'
            ? 'text.primary'
            : 'primary.contrastText',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton 
              edge="start" 
              color="inherit" 
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Avatar 
            src={user?.avatar} 
            sx={{ 
              mr: 2,
              bgcolor: theme.palette.mode === 'dark' 
                ? 'primary.main' 
                : 'primary.dark'
            }}
          >
            {user?.username?.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {isMobile && selectedContact ? selectedContact.username : 'AuniChat'}
          </Typography>
          <Badge badgeContent={friendRequests.length} color="error" sx={{ mr: 2 }}>
            <IconButton color="inherit" onClick={() => setFriendRequestsOpen(true)}>
              <PersonAddIcon />
            </IconButton>
          </Badge>
          {!isMobile && (
            <>
              <IconButton color="inherit" onClick={handleSettings}>
                <SettingsIcon />
              </IconButton>
              <IconButton color="inherit" onClick={handleLogout}>
                <LogoutIcon />
              </IconButton>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ 
        display: 'flex', 
        flexGrow: 1, 
        overflow: 'hidden',
        bgcolor: 'background.default'
      }}>
        {/* Contacts List */}
        {isMobile ? (
          <Drawer
            variant="temporary"
            anchor="left"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            sx={{
              width: 320,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: 320,
                boxSizing: 'border-box',
                bgcolor: 'background.paper',
                borderRight: `1px solid ${theme.palette.divider}`
              },
            }}
          >
            <ContactListContent />
          </Drawer>
        ) : (
          <Box sx={{ 
            width: 320, 
            flexShrink: 0, 
            bgcolor: 'background.paper',
            borderRight: `1px solid ${theme.palette.divider}`
          }}>
            <ContactListContent />
          </Box>
        )}

        {/* Chat Area */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          height: '100%',
          bgcolor: 'background.default'
        }}>
          {selectedContact ? (
            <>
              {/* Messages Area */}
              <Box sx={{ 
                flexGrow: 1, 
                overflow: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'background.default'
                  : '#f8f9fa'
              }}>
                {messages.map((message) => (
                  <MessageBubble
                    key={message._id}
                    message={message}
                    isOwn={message.sender._id === user.id}
                  />
                ))}
                <div ref={messagesEndRef} />
              </Box>

              {/* Message Input */}
              <Box sx={{ 
                p: isMobile ? 1 : 2, 
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper'
              }}>
                <form onSubmit={handleSendMessage}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                    <IconButton
                      onClick={(e) => setMediaUploadAnchor(e.currentTarget)}
                      disabled={!selectedContact}
                      sx={{ 
                        mb: isMobile ? 0.5 : 1,
                        color: 'text.secondary'
                      }}
                    >
                      <AttachFileIcon />
                    </IconButton>
                    <TextField
                      fullWidth
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      multiline
                      maxRows={4}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          bgcolor: theme.palette.mode === 'dark'
                            ? 'background.default'
                            : 'background.paper',
                          '&:hover': {
                            bgcolor: theme.palette.mode === 'dark'
                              ? 'action.hover'
                              : 'background.default'
                          },
                          '&.Mui-focused': {
                            bgcolor: theme.palette.mode === 'dark'
                              ? 'background.paper'
                              : 'background.default'
                          }
                        }
                      }}
                    />
                    <Zoom in={Boolean(newMessage.trim())}>
                      <Fab 
                        color="primary" 
                        size={isMobile ? "small" : "medium"}
                        onClick={handleSendMessage}
                        sx={{ ml: 1 }}
                      >
                        <SendIcon />
                      </Fab>
                    </Zoom>
                  </Box>
                </form>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                bgcolor: theme.palette.mode === 'dark'
                  ? 'background.default'
                  : '#f8f9fa',
                p: 3,
                textAlign: 'center'
              }}
            >
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                color="text.secondary"
              >
                Select a contact to start chatting
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Media Upload Menu */}
      <Menu
        anchorEl={mediaUploadAnchor}
        open={Boolean(mediaUploadAnchor)}
        onClose={() => setMediaUploadAnchor(null)}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 180,
            bgcolor: 'background.paper',
            '& .MuiMenuItem-root': {
              py: 1.5,
              color: 'text.primary'
            }
          }
        }}
      >
        <MenuItem onClick={() => {
          fileInputRef.current?.click();
          setMediaUploadAnchor(null);
        }}>
          Upload Photo
        </MenuItem>
        <MenuItem onClick={() => {
          fileInputRef.current?.click();
          setMediaUploadAnchor(null);
        }}>
          Upload Video
        </MenuItem>
      </Menu>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*,video/*"
        style={{ display: 'none' }}
        onChange={handleMediaUpload}
      />

      {/* Settings Menu */}
      <Menu
        anchorEl={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={handleCloseSettings}
        PaperProps={{
          elevation: 3,
          sx: { 
            minWidth: 200,
            bgcolor: 'background.paper',
            '& .MuiMenuItem-root': {
              color: 'text.primary'
            }
          }
        }}
      >
        <MenuItem onClick={handleSettings}>
          <SettingsIcon sx={{ mr: 2, color: 'text.secondary' }} /> Settings
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 2, color: 'text.secondary' }} /> Logout
        </MenuItem>
      </Menu>

      {/* Friend Requests Dialog */}
      <Dialog 
        open={friendRequestsOpen} 
        onClose={() => setFriendRequestsOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary'
          }
        }}
      >
        <DialogTitle>Friend Requests</DialogTitle>
        <DialogContent>
          {friendRequests.length === 0 ? (
            <Typography color="text.secondary">No pending friend requests</Typography>
          ) : (
            friendRequests.map((request) => (
              <Box 
                key={request._id} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  p: 1,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <Avatar 
                  src={request.from.avatar} 
                  sx={{ 
                    mr: 2,
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'primary.main'
                      : 'primary.dark'
                  }}
                >
                  {request.from.username?.charAt(0).toUpperCase()}
                </Avatar>
                <Typography sx={{ flexGrow: 1 }}>{request.from.username}</Typography>
                <IconButton 
                  onClick={() => handleAcceptFriend(request.from._id)} 
                  color="success"
                  size={isMobile ? "small" : "medium"}
                >
                  <CheckIcon />
                </IconButton>
                <IconButton 
                  onClick={() => handleRejectFriend(request.from._id)} 
                  color="error"
                  size={isMobile ? "small" : "medium"}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        TransitionComponent={Slide}
        sx={{
          bottom: isMobile ? 70 : 24
        }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ 
            width: '100%',
            bgcolor: theme.palette.mode === 'dark'
              ? 'background.paper'
              : 'background.default'
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Chat; 